<script setup>
import StatAdjustmentModal from './StatAdjustmentModal.vue'
import TeamRosterGrid from './TeamRosterGrid.vue'
import ProjectionFilters from './ProjectionFilters.vue'
import NotificationSystem from './NotificationSystem.vue'
import { useProjectionsStore } from '../stores/projections'
import { useNotifications } from '../composables/useNotifications'
import AutomaticPreProjectionsModal from './AutomaticPreProjectionsModal.vue'
import { GlobalParametersModal } from './filters'
const projectionsStore = useProjectionsStore()
const { notifications, dismissNotification, success, error } = useNotifications()

const emit = defineEmits(['create', 'filter-change'])

const props = defineProps({
  fixtureData: {
    type: Object,
    required: true,
    validator: (value) => {
      return value && (
        'home_team' in value ||
        'away_team' in value ||
        'league' in value
      )
    }
  },
  leagues: {
    type: Array,
    default: () => []
  },
  seasons: {
    type: Array,
    default: () => []
  }
})

import { ref, computed, nextTick, watch } from 'vue'

// Watch for save success/error and create notifications
watch(() => projectionsStore.saveSuccess, (newVal) => {
  if (newVal) {
    success('Projections have been saved successfully')
    // Reset the store state
    setTimeout(() => {
      projectionsStore.saveSuccess = false
    }, 100)
  }
})

watch(() => projectionsStore.saveError, (newVal) => {
  if (newVal) {
    error(newVal)
    // Reset the store state after a delay
    setTimeout(() => {
      projectionsStore.saveError = null
    }, 100)
  }
})

const getPlayerProjection = (playerId) => {
  if (!projectionsStore.projectionsData?.fixture_projections) return null;
  return projectionsStore.projectionsData.fixture_projections.find(
    proj => proj.player?.id === playerId
  );
};

const getPlayerStat = (playerId, statKey) => {
  const proj = getPlayerProjection(playerId);
  return proj && proj[statKey] != null ? proj[statKey] : '—';
};

const getPlayerProjectedMinutes = (playerId, fallback) => {
  const proj = getPlayerProjection(playerId);
  return proj && proj.projected_minutes != null ? proj.projected_minutes : (fallback || 0);
};

const getRosterWithMinutes = (roster) => {
  return roster
    .map(playerObj => ({
      ...playerObj,
      projected_minutes: ref(getPlayerProjectedMinutes(playerObj.player?.id, playerObj.projected_minutes)),
      projection: getPlayerProjection(playerObj.player?.id)
    }));
}

const homeRoster = computed(() => getRosterWithMinutes(projectionsStore.projectionsData?.home_team_roster || []))
const awayRoster = computed(() => getRosterWithMinutes(projectionsStore.projectionsData?.away_team_roster || []))

const statKeys = [
  { key: 'points_per_30', label: 'Points', market: 'points' },
  { key: 'total_rebounds_per_30', label: 'Rebounds', market: 'rebounds' },
  { key: 'assists_per_30', label: 'Assists', market: 'assists' },
  { key: 'three_point_field_goal_per_30', label: '3PT', market: 'three_point_field_goal' },
  { key: 'pra_per_30', label: 'PRA', market: 'pra' },
  { key: 'turnovers_per_30', label: 'Turnovers', market: 'turnovers' },
  { key: 'blocks_per_30', label: 'Blocks', market: 'blocks' },
  { key: 'steals_per_30', label: 'Steals', market: 'steals' },
]

function getPerThirtyValue(playerId, statKey) {
  const proj = getPlayerProjection(playerId);
  let value = null, bg = 'bg-gray-200', text = 'text-black';
  if (!proj) return { value: '—', bg, text };
  if (proj[`${statKey}_trim`] != null) {
    value = proj[`${statKey}_trim`];
    bg = 'bg-blue-500';
    text = 'text-white';
  } else if (proj[`${statKey}_manual`] != null) {
    value = proj[`${statKey}_manual`];
    bg = 'bg-red-500';
    text = 'text-white';
  } else if (proj[`${statKey}_auto`] != null && proj[`${statKey}_auto`] !== '' && proj[`${statKey}_auto`] > 0) {
    value = proj[`${statKey}_auto`];
    bg = 'bg-green-500';
    text = 'text-white';
  } else if (proj[`${statKey}_original`] != null) {
    value = proj[`${statKey}_original`];
    bg = 'bg-gray-300';
    text = 'text-black';
  } else {
    value = '—';
  }
  // Format to 2 decimal places if value is a number
  const num = Number(value);
  const display = value !== '—' && !isNaN(num) ? num.toFixed(2) : value;
  return { value: display, bg, text };
}

function getRawStatValue(playerId, statKey) {
  const proj = getPlayerProjection(playerId);
  // Remove _per_30 from statKey for raw value
  const rawKey = statKey.replace(/_per_30$/, '');
  if (!proj || proj[rawKey] == null) return '—';
  const num = Number(proj[rawKey]);
  return !isNaN(num) ? num.toFixed(2) : proj[rawKey];
}

function getTotal(roster, statKey) {
  return roster.reduce((sum, p) => {
    const val = getPlayerStat(p.player.id, statKey)
    return sum + (isNaN(Number(val)) ? 0 : Number(val))
  }, 0).toFixed(2)
}

const getTotalProjectedMinutes = (roster) => {
  return roster.reduce((sum, p) => sum + Number(p.projected_minutes.value || 0), 0)
}

const showAdjustmentModal = ref(false)
const showAutomaticModal = ref(false)
const showGlobalModal = ref(false)
const adjustmentModalData = ref({ player: null, statKey: '', statLabel: '' })
const adjustmentModalPosition = ref({ top: 0, left: 0 })

function openAutomaticSettingsInNewWindow() {
  const leagueId = projectionsStore.projectionsData?.league?.id
  const teamId = projectionsStore.projectionsData?.home_team?.id
  const fixtureId = projectionsStore.projectionsData?.id
  
  if (!leagueId || !teamId || !fixtureId) {
    console.error('Missing required IDs for automatic settings')
    return
  }
  
  const url = `/automatic-projections-settings/${leagueId}/${teamId}/${fixtureId}`
  window.open(url, '_blank', 'width=1400,height=900,scrollbars=yes,resizable=yes')
}

function handleGlobalParametersUpdated(parameters) {
  console.log('Global parameters updated:', parameters)
  // The store is already updated by the modal, so we just need to refresh the data
  // Note: The store will be updated by the modal's applyGlobalParameters function
}

function openAdjustmentModal(player, statKey, statLabel, event) {
  adjustmentModalData.value = { player, statKey, statLabel }
  showAdjustmentModal.value = true
  // Position the modal to the right of the clicked pill, vertically centered
  nextTick(() => {
    const rect = event.target.getBoundingClientRect()
    const modalHeight = 320 // px, approximate
    adjustmentModalPosition.value = {
      top: rect.top + window.scrollY + rect.height / 2 - modalHeight / 2,
      left: rect.right + window.scrollX + 12 // 12px gap to the right
    }
  })
}
function closeAdjustmentModal() {
  showAdjustmentModal.value = false
}

const selectedFilters = ref({
  leagues: [],
  seasons: []
})

const isCreating = ref(false)

function handleFilterChange(filters) {
  selectedFilters.value = filters
  emit('filter-change', filters)
}

function handleCreate() {
  if (isCreating.value) return
  
  isCreating.value = true
  const payload = {
    fixture_id: props.fixtureData.id,
    leagues: selectedFilters.value.leagues,
    seasons: selectedFilters.value.seasons
  }
  console.log('Create payload:', payload)
  emit('create', payload)
  
  // Reset after a delay to allow re-clicking if needed
  setTimeout(() => {
    isCreating.value = false
  }, 3000)
}


</script>

<template>
  <div class="flex flex-col gap-0">
    <!-- Notification System -->
    <NotificationSystem 
      :notifications="notifications" 
      @dismiss="dismissNotification" 
    />
    
    <div v-if="projectionsStore.projectionsData?.fixture_projections?.length > 0" class="flex items-center gap-4 py-3 px-4 bg-white rounded-lg shadow-md mb-3">
      <i class="fas fa-check-circle text-xl text-green-500"></i>
      <span class="text-sm font-medium text-gray-900">Projections Available</span>
      <div class="flex gap-2">
        <button
          @click="projectionsStore.saveToBalancedLines"
          :disabled="projectionsStore.fixtureProjectionsConfirmationLoadingStatus"
          :class="{
            'opacity-50 cursor-not-allowed': projectionsStore.fixtureProjectionsConfirmationLoadingStatus,
            'opacity-100 cursor-pointer': !projectionsStore.fixtureProjectionsConfirmationLoadingStatus
          }"
          class="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors"
        >
          <i class="fas fa-save mr-1"></i>
          <span v-if="projectionsStore.fixtureProjectionsConfirmationLoadingStatus">
            <i class="fas fa-spinner fa-spin mr-1"></i>
            Saving...
          </span>
          <span v-else>
            Confirm Projections
          </span>
        </button>
      </div>
    </div>
    <!-- {{ projectionsStore.projectionsData }} -->
    <!-- {{ fixtureData.fixture_projections }} -->
    <!-- Filters Section -->
    <ProjectionFilters
    v-if="projectionsStore.projectionsData?.fixture_projections?.length === 0"

      :initial-league="projectionsStore.projectionsData?.league?.id?.toString()"
      :initial-season="projectionsStore.projectionsData?.season"
      @filter-change="handleFilterChange"
    />

    <!-- Global Filter Settings Section -->
     <div class="flex items-center justify-between mb-3">
      <div class="mb-4">
      <div class="flex items-center justify-between mb-3">
        <button
          @click="showGlobalModal = true"
          class="inline-flex items-center px-4 py-2 bg-pink-600 text-white rounded-lg shadow hover:bg-pink-700 transition-colors font-medium"
        >
          <i class="fas fa-cog mr-2"></i>
          Global Filters
        </button>
      </div>
    </div>


    <!-- Automatic Settings Buttons - Always Visible -->
    <div class="flex justify-end gap-2 mb-4">
      <button
        @click="showAutomaticModal = true"
        class="inline-flex items-center justify-center p-3 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition-colors"
        title="Automatic Settings (Popup)"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
      </button>
      <button
        @click="openAutomaticSettingsInNewWindow"
        class="inline-flex items-center justify-center p-3 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors"
        title="Open in New Window"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
        </svg>
      </button>
    </div>
     </div>
   


    <!-- Automatic Settings Modal -->
    <AutomaticPreProjectionsModal 
      :show="showAutomaticModal"
      :league-id="projectionsStore.projectionsData?.league?.id"
      :team-id="projectionsStore.projectionsData?.home_team?.id"
      :fixture-id="projectionsStore.projectionsData?.id"
      @close="showAutomaticModal = false"
    />

    <!-- Global Parameters Modal -->
    <GlobalParametersModal
      :show="showGlobalModal"
      @close="showGlobalModal = false"
      @global-parameters-updated="handleGlobalParametersUpdated"
    />
      
    <!-- Create Projections Section -->
    <div v-if="!projectionsStore.projectionsData?.fixture_projections || !projectionsStore.projectionsData.fixture_projections.length" class="flex flex-col items-center justify-center py-12 px-4 bg-white rounded-2xl shadow-lg mb-4">
      <div class="text-center">
        <div class="mb-4">
          <i class="fas fa-chart-line text-4xl text-blue-300"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">No Projections Available</h3>
        <p class="text-gray-500 mb-6">Create projections to start analyzing player performance.</p>
        <div class="flex gap-3">
          <button
            @click="handleCreate"
            :disabled="isCreating"
            :class="{
              'opacity-50 cursor-not-allowed': isCreating,
              'hover:bg-blue-700': !isCreating
            }"
            class="inline-flex items-center px-5 py-2 bg-blue-600 text-white rounded-lg shadow transition-colors font-medium"
          >
            <i :class="isCreating ? 'fas fa-spinner fa-spin mr-2' : 'fas fa-plus mr-2'"></i>
            <span v-if="isCreating">Creating...</span>
            <span v-else>Create New Projections</span>
          </button>
          <button
            @click="showAutomaticModal = true"
            class="inline-flex items-center px-5 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition-colors font-medium"
          >
            <i class="fas fa-cogs mr-2"></i>
            Automatic Settings
          </button>
        </div>
      </div>
    </div>
    <!-- <pre>{{ projectionsStore.projectionsData }}</pre> -->
    <!-- Home Team Section -->
    <div class="flex flex-col gap-4 mt-4 relative " >
      <div class="hidden absolute inset-0 bg-white bg-opacity-30 z-40 flex flex-col items-center justify-center">
        <span class="text-lg text-gray-700 font-semibold mb-3 bg-white p-2 rounded-lg">Game is assigned to TEst.</span>
        <button
          class="inline-flex items-center px-5 py-2 bg-pink-600 text-white rounded-lg shadow hover:bg-pink-700 transition-colors font-medium"
        >
          <i class="fas fa-user-check mr-2"></i>
          Take Over
        </button>
      </div>
      <div class="font-bold text-xl mb-3 flex items-center gap-2">
        <span class="inline-block w-2 h-6 rounded bg-blue-500"></span>
        <span class="text-blue-700">{{ projectionsStore.projectionsData?.home_team?.team_name || 'Home Team' }}</span>
      </div>
      <TeamRosterGrid
        :roster="homeRoster"
        team-type="A"
        team-color="blue"
        :stat-keys="statKeys"
      />
    </div>

    <!-- Away Team Section -->
    <div>
      <div class="font-bold text-xl mb-3 flex items-center gap-2">
        <span class="inline-block w-2 h-6 rounded bg-red-500"></span>
        <span class="text-red-700">{{ projectionsStore.projectionsData?.away_team?.team_name || 'Away Team' }}</span>
      </div>
      <TeamRosterGrid
        :roster="awayRoster"
        team-type="B"
        team-color="red"
        :stat-keys="statKeys"
      />
    </div>
  </div>

  <!-- Global Parameters Modal -->
  <GlobalParametersModal
    :show="showGlobalModal"
    :fixture-id="projectionsStore.projectionsData?.id"
    @close="showGlobalModal = false"
    @globalParametersUpdated="handleGlobalParametersUpdated"
  />
</template> 